import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-at";

/**
 * Lazy import that survives stale deploys: if a code-split chunk 404s
 * (old index.html referencing removed hashed files), retry once, then
 * force a single hard reload to pick up the new build.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      // second chance: transient network hiccup
      try {
        await new Promise((r) => setTimeout(r, 500));
        return await factory();
      } catch {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        if (Date.now() - last > 15000) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          // keep the promise pending while reloading
          return await new Promise<{ default: T }>(() => {});
        }
        throw error;
      }
    }
  });
}
